/**
 * SkillsView -- Skills & Pensum management + user progress.
 * Shows skill cards with progress bars, pensum plans, and user achievements.
 * Artifact version: v1.0
 *
 * @module presentation/views/SkillsView
 */

import { LearningApiClient } from '../../infrastructure/api/LearningApiClient.js';
import { MissionApiClient } from '../../infrastructure/api/MissionApiClient.js';
import { AgentBadgeApiClient } from '../../infrastructure/api/AgentBadgeApiClient.js';
import { renderNxList, bindNxListEvents } from '../shared/NxListViewer.js';
import { buildLearningEngine } from '../shared/NxLearningEngine.js';
import { buildFlashCards } from '../shared/NxFlashCards.js';

// Deuda 4: el escape local solo cambiaba "<" y este archivo lo usa dentro de atributos con comilla doble.
import { esc as escHtml } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

const MISSION_META_LABELS = {
  in_progress: 'en progreso', active: 'activa', por_cerrar: 'por cerrar', draft: 'borrador',
  completed: 'completada', archived: 'archivada'
};

function parseMissionMetadata(m) {
  let meta = m.metadata ?? m.Metadata;
  if (meta == null) return {};
  if (typeof meta === 'string') {
    try { return JSON.parse(meta); } catch { return {}; }
  }
  return typeof meta === 'object' ? meta : {};
}

function formatMissionProgress(m) {
  const p = m.progressPercent ?? m.progress;
  if (typeof p === 'number' && !Number.isNaN(p)) return `${Math.round(p)}%`;
  const tc = m.completedTaskCount ?? m.completedTasks;
  const tt = (m.taskIds || []).length;
  if (typeof tc === 'number' && tt > 0) return `${tc}/${tt} tareas`;
  if (tt > 0) return `${tt} tarea(s)`;
  if (m.status === 'completed') return '100%';
  return '—';
}

function linkedMissionsHtml(skillCode, missions) {
  const linked = missions.filter(mm => parseMissionMetadata(mm).skill_code === skillCode);
  if (linked.length === 0) return '';
  return `
      <div style="font-size:0.875rem;font-weight:600;margin:12px 0 4px">Misiones asociadas (${linked.length})</div>
      <div style="max-height:180px;overflow-y:auto;border:1px solid #21262d;border-radius:6px;padding:6px">
        ${linked.map(m => {
    const st = m.status || '';
    const lbl = MISSION_META_LABELS[st] || st;
    const prog = formatMissionProgress(m);
    return `<div style="padding:6px 4px;border-bottom:1px solid #161b22;font-size:0.875rem">
          <div style="display:flex;justify-content:space-between;gap:8px;align-items:start">
            <a href="#/missions/${m.id}" class="item-used-link" style="font-weight:600"><code>${escHtml(m.code || '')}</code> ${escHtml(m.title || '')}</a>
          </div>
          <div style="margin-top:4px;color:#8b949e">
            <span class="task-tag" style="background:#21262d;color:#8b949e;font-size:0.875rem">${escHtml(lbl)}</span>
            <span style="margin-left:6px">Progreso: ${escHtml(prog)}</span>
          </div>
        </div>`;
  }).join('')}
      </div>`;
}

const LEVEL_NAMES = ['Aprendiz', 'Iniciado', 'Explorador', 'Guardian', 'Maestro'];

const CAT_COLORS = {
  marketing: '#f97583',
  productividad: '#58a6ff',
  nexo: '#238636'
};

let currentTab = 'skills';

export async function renderSkills(container) {
  currentTab = 'skills';

  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>Skills & Pensum</h1>
          <p style="font-size:0.875rem;color:#8b949e;margin-top:2px">Habilidades y planes de estudio del ecosistema</p>
        </div>
      </div>

      <div class="task-filter-bar" id="nx-skills-tabs">
        <button class="btn-filter active" data-sk-tab="skills">Mis Skills</button>
        <button class="btn-filter" data-sk-tab="agents">Badges</button>
        <button class="btn-filter" data-sk-tab="pensum">Planes de Estudio</button>
        <button class="btn-filter" data-sk-tab="catalog">Catalogo</button>
      </div>

      <div id="nx-skills-content" style="margin-top:12px"></div>
    </section>`;

  const $tabs = document.getElementById('nx-skills-tabs');
  const $content = document.getElementById('nx-skills-content');

  $tabs.addEventListener('click', (e) => {
    const btn = e.target.closest('.btn-filter');
    if (!btn) return;
    $tabs.querySelectorAll('.btn-filter').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    currentTab = btn.dataset.skTab;
    renderTab($content, currentTab);
  });

  renderTab($content, currentTab);
}

async function renderTab($el, tab) {
  if (tab === 'skills') await renderMySkills($el);
  else if (tab === 'agents') await renderAgentSkills($el);
  else if (tab === 'pensum') await renderPensum($el);
  else await renderCatalog($el);
}

async function renderMySkills($el) {
  $el.innerHTML = '<p class="empty-state">Cargando skills...</p>';

  const res = await LearningApiClient.getSkillProgress();
  if (!res.ok) {
    $el.innerHTML = `<p class="empty-state">Error: ${res.error}</p>`;
    return;
  }

  const skills = res.data?.data || [];

  const cards = skills.map(s => {
    const answered = parseInt(s.answered) || 0;
    const correct = parseInt(s.correct) || 0;
    const accuracy = parseFloat(s.accuracy) || 0;
    const required = parseInt(s.questions_required) || 10;
    const reqAccuracy = parseInt(s.accuracy_required) || 70;
    const nxs = parseInt(s.nexos_reward) || 0;

    const progressPct = Math.min(100, Math.round((answered / required) * 100));
    const unlocked = answered >= required && accuracy >= reqAccuracy;
    const color = CAT_COLORS[s.category] || '#6e7681';

    return `
      <div class="artifact-item" style="padding:16px;border-left:3px solid ${color}">
        <div style="display:flex;justify-content:space-between;align-items:start">
          <div>
            <div style="font-weight:700;font-size:0.95rem">${escHtml(s.name)}</div>
            <div style="font-size:0.875rem;color:#6e7681;margin-top:2px">
              <span class="task-tag" style="background:${color}22;color:${color}">${escHtml(s.category)}</span>
              <code style="font-size:0.875rem;margin-left:4px">${escHtml(s.code)}</code>
            </div>
          </div>
          <div style="text-align:right">
            ${unlocked
              ? '<span style="color:#238636;font-weight:700;font-size:0.875rem">\u2713 Desbloqueado</span>'
              : `<span style="color:#6e7681;font-size:0.875rem">${answered}/${required} preguntas</span>`}
            <div style="font-size:0.875rem;color:#d29922;margin-top:2px">+${nxs} NXS</div>
          </div>
        </div>
        <div style="margin-top:10px">
          <div class="progress-bar" style="height:6px;background:#21262d;border-radius:3px;overflow:hidden">
            <div style="width:${progressPct}%;height:100%;background:${unlocked ? '#238636' : color};border-radius:3px;transition:width 0.3s"></div>
          </div>
          <div style="display:flex;justify-content:space-between;margin-top:4px;font-size:0.875rem;color:#6e7681">
            <span>${progressPct}% completado</span>
            <span>Precision: ${accuracy}% ${accuracy >= reqAccuracy ? '\u2713' : `(necesitas ${reqAccuracy}%)`}</span>
          </div>
        </div>
      </div>`;
  }).join('');

  const totalUnlocked = skills.filter(s => {
    const a = parseInt(s.answered) || 0;
    const acc = parseFloat(s.accuracy) || 0;
    return a >= (parseInt(s.questions_required) || 10) && acc >= (parseInt(s.accuracy_required) || 70);
  }).length;

  $el.innerHTML = `
    <div class="banco-stats-grid" style="margin-bottom:12px">
      <div class="banco-stat-card">
        <div class="banco-stat-value">${skills.length}</div>
        <div class="banco-stat-label">Skills totales</div>
      </div>
      <div class="banco-stat-card">
        <div class="banco-stat-value" style="color:#238636">${totalUnlocked}</div>
        <div class="banco-stat-label">Desbloqueados</div>
      </div>
      <div class="banco-stat-card">
        <div class="banco-stat-value" style="color:#d29922">${skills.length - totalUnlocked}</div>
        <div class="banco-stat-label">En progreso</div>
      </div>
    </div>
    <div class="artifact-list" style="gap:8px">${cards || '<p class="empty-state">Sin skills disponibles</p>'}</div>`;
}

async function renderPensum($el) {
  $el.innerHTML = '<p class="empty-state">Cargando planes...</p>';

  const [pensumRes, skillsRes] = await Promise.all([
    LearningApiClient.getPensum(),
    LearningApiClient.getSkills()
  ]);

  if (!pensumRes.ok) {
    $el.innerHTML = `<p class="empty-state">Error: ${pensumRes.error}</p>`;
    return;
  }

  const pensums = pensumRes.data?.data || [];
  const skills = skillsRes.ok ? (skillsRes.data?.data || []) : [];
  const skillMap = new Map(skills.map(s => [s.code, s]));

  const cards = pensums.map(p => {
    const codes = typeof p.skill_codes === 'string' ? JSON.parse(p.skill_codes) : (p.skill_codes || []);
    const totalNxs = codes.reduce((sum, c) => sum + ((skillMap.get(c)?.nexos_reward) || 0), 0);

    return `
      <div class="artifact-item pensum-card" style="padding:16px;cursor:pointer" data-pensum-code="${escHtml(p.code)}" data-skill-codes="${codes.join(',')}">
        <div style="display:flex;justify-content:space-between;align-items:start">
          <div>
            <div style="font-weight:700;font-size:0.95rem">${escHtml(p.name)}</div>
            <div style="font-size:0.875rem;color:#8b949e;margin-top:4px">${escHtml(p.description || '')}</div>
          </div>
          <div style="text-align:right;white-space:nowrap">
            <div style="font-size:0.875rem;color:#d29922">${codes.length} skill${codes.length !== 1 ? 's' : ''} &middot; +${totalNxs} NXS</div>
            <div style="font-size:0.875rem;color:#6e7681;margin-top:2px">Click para ver clases &darr;</div>
          </div>
        </div>
        <div style="margin-top:8px;display:flex;flex-wrap:wrap;gap:6px">
          ${codes.map(c => {
            const sk = skillMap.get(c);
            const color = sk ? (CAT_COLORS[sk.category] || '#6e7681') : '#6e7681';
            return `<span class="task-tag" style="background:${color}22;color:${color}">${escHtml(sk?.name || c)}</span>`;
          }).join('')}
        </div>
        <div class="pensum-detail" id="pensum-detail-${escHtml(p.code)}" style="display:none;margin-top:12px;border-top:1px solid #21262d;padding-top:12px"></div>
      </div>`;
  }).join('');

  $el.innerHTML = `
    <div class="detail-section">
      <h2>Planes de Estudio</h2>
      <p style="font-size:0.875rem;color:#8b949e;margin-bottom:8px">Cada plan es una secuencia de clases. Click en un plan para ver el programa clase a clase.</p>
      <div class="artifact-list" style="gap:8px">${cards || '<p class="empty-state">Sin planes disponibles</p>'}</div>
    </div>`;

  // Click handler: cargar clases del pensum
  $el.querySelectorAll('.pensum-card').forEach(card => {
    card.addEventListener('click', async () => {
      const code = card.dataset.pensumCode;
      const detail = document.getElementById(`pensum-detail-${code}`);
      if (!detail) return;

      // Toggle
      if (detail.style.display !== 'none') {
        detail.style.display = 'none';
        return;
      }

      detail.style.display = 'block';
      detail.innerHTML = '<p style="font-size:0.875rem;color:#6e7681">Cargando clases...</p>';

      const skillCodes = (card.dataset.skillCodes || '').split(',').filter(Boolean);
      // Cargar topics de cada skill del pensum
      const topicsBySkill = [];
      for (const sc of skillCodes) {
        const tRes = await LearningApiClient.getTopics(sc);
        const sk = skillMap.get(sc);
        const topics = tRes.ok ? (tRes.data?.data || []) : [];
        topicsBySkill.push({ skill: sk, topics });
      }

      let html = '';
      for (const { skill, topics } of topicsBySkill) {
        const color = skill ? (CAT_COLORS[skill.category] || '#6e7681') : '#6e7681';
        html += `<div style="margin-bottom:16px">
          <div style="font-weight:700;font-size:0.875rem;margin-bottom:6px">
            <span class="task-tag" style="background:${color}22;color:${color}">${escHtml(skill?.category || '')}</span>
            ${escHtml(skill?.name || '')}
            <span style="color:#d29922;font-size:0.875rem;margin-left:6px">+${skill?.nexos_reward || 0} NXS</span>
          </div>`;

        if (topics.length === 0) {
          html += '<p style="font-size:0.875rem;color:#484f58;margin-left:12px">Sin clases definidas</p>';
        } else {
          topics.forEach((t, i) => {
            const order = t.sort_order || (i + 1);
            const prereq = t.prerequisite_topic_code;
            const summary = t.material_summary || t.materialSummary || '';
            const sources = t.material_sources || t.materialSources || [];
            const srcList = typeof sources === 'string' ? JSON.parse(sources) : (Array.isArray(sources) ? sources : []);
            const qCount = t.question_count || 0;

            html += `
            <div style="margin-left:12px;padding:10px 12px;margin-bottom:6px;background:#161b22;border-radius:6px;border-left:3px solid ${prereq ? '#21262d' : color}">
              <div style="display:flex;justify-content:space-between;align-items:start">
                <div>
                  <span style="font-weight:700;font-size:0.875rem;color:${color}">Clase ${order}</span>
                  <span style="font-weight:600;font-size:0.875rem;margin-left:6px">${escHtml(t.name)}</span>
                </div>
                <div style="font-size:0.875rem;color:#6e7681;white-space:nowrap">
                  ${qCount} preguntas
                  ${prereq ? `<span style="margin-left:4px" title="Requiere aprobar clase anterior">&#128274;</span>` : `<span style="color:#238636" title="Disponible">&#128275;</span>`}
                </div>
              </div>
              ${summary ? `<p style="font-size:0.875rem;color:#c9d1d9;margin:8px 0 0;line-height:1.4">${escHtml(summary)}</p>` : ''}
              ${srcList.length > 0 ? `
                <div style="margin-top:8px">
                  <span style="font-size:0.875rem;font-weight:600;color:#6e7681">Material de referencia:</span>
                  <div style="margin-top:4px;display:flex;flex-direction:column;gap:2px">
                    ${srcList.map(s => `<span style="font-size:0.875rem;color:#58a6ff;font-family:monospace">&#128196; ${escHtml(s)}</span>`).join('')}
                  </div>
                </div>` : ''}
              ${prereq ? `<div style="margin-top:6px;font-size:0.875rem;color:#d29922">&#9888; Prerequisito: aprobar ${escHtml(prereq)} con 75%+</div>` : ''}
              <div style="margin-top:10px;display:flex;gap:8px;flex-wrap:wrap">
                <button class="btn-primary btn-sm pensum-start-btn" data-mode="quiz" data-category="${escHtml(skill?.category || '')}" style="font-size:0.875rem;padding:4px 12px">&#9889; Quiz</button>
                <button class="btn-ghost btn-sm pensum-start-btn" data-mode="flashcards" data-category="${escHtml(skill?.category || '')}" style="font-size:0.875rem;padding:4px 12px">&#127183; Tarjetas V/F</button>
              </div>
            </div>`;
          });
        }
        html += '</div>';
      }

      detail.innerHTML = html;

      // Handler: botones Quiz / Tarjetas V/F
      detail.querySelectorAll('.pensum-start-btn').forEach(btn => {
        btn.addEventListener('click', (ev) => {
          ev.stopPropagation();
          const mode = btn.dataset.mode;
          const category = btn.dataset.category;
          openClassActivity(mode, category);
        });
      });
    });
  });
}

// ─── Modal de actividad (Quiz o Tarjetas) ────────────────────────────────────

let activeEngine = null;

function openClassActivity(mode, category) {
  // Limpiar engine anterior si existe
  if (activeEngine) { activeEngine.destroy(); activeEngine = null; }

  // Crear overlay modal
  const overlay = document.createElement('div');
  overlay.id = 'pensum-activity-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.75);z-index:1000;display:flex;align-items:center;justify-content:center;padding:20px';

  const modal = document.createElement('div');
  modal.style.cssText = 'background:#0d1117;border:1px solid #30363d;border-radius:12px;width:100%;max-width:600px;max-height:85vh;overflow-y:auto;padding:0';

  // Header con tabs para cambiar modo
  const quizActive = mode === 'quiz';
  modal.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;padding:12px 16px;border-bottom:1px solid #21262d">
      <div style="display:flex;gap:0">
        <button class="activity-mode-btn" data-amode="quiz" style="padding:6px 14px;border:none;cursor:pointer;font-size:0.875rem;font-weight:600;border-radius:4px 0 0 4px;${quizActive ? 'background:#238636;color:white' : 'background:#21262d;color:#8b949e'}">&#9889; Quiz</button>
        <button class="activity-mode-btn" data-amode="flashcards" style="padding:6px 14px;border:none;cursor:pointer;font-size:0.875rem;font-weight:600;border-radius:0 4px 4px 0;${!quizActive ? 'background:#d29922;color:white' : 'background:#21262d;color:#8b949e'}">&#127183; Tarjetas V/F</button>
      </div>
      <button id="pensum-activity-close" style="background:none;border:none;color:#8b949e;cursor:pointer;font-size:1.2rem;padding:4px 8px">&times;</button>
    </div>
    <div id="pensum-activity-container" style="padding:16px;min-height:300px"></div>`;

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  const $container = modal.querySelector('#pensum-activity-container');

  // Cerrar modal
  modal.querySelector('#pensum-activity-close').addEventListener('click', () => {
    if (activeEngine) { activeEngine.destroy(); activeEngine = null; }
    overlay.remove();
  });
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      if (activeEngine) { activeEngine.destroy(); activeEngine = null; }
      overlay.remove();
    }
  });

  // Cambiar modo
  modal.querySelectorAll('.activity-mode-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      if (activeEngine) { activeEngine.destroy(); activeEngine = null; }
      modal.querySelectorAll('.activity-mode-btn').forEach(b => { b.style.background = '#21262d'; b.style.color = '#8b949e'; });
      if (btn.dataset.amode === 'quiz') {
        btn.style.background = '#238636'; btn.style.color = 'white';
      } else {
        btn.style.background = '#d29922'; btn.style.color = 'white';
      }
      launchActivity(btn.dataset.amode, category, $container);
    });
  });

  // Lanzar actividad inicial
  launchActivity(mode, category, $container);
}

async function launchActivity(mode, category, $container) {
  $container.innerHTML = '<p style="text-align:center;color:#8b949e;padding:40px 0">Cargando...</p>';

  if (mode === 'quiz') {
    activeEngine = await buildLearningEngine($container, {
      category,
      context: `pensum-${category}`,
      onComplete: () => {}
    });
  } else {
    activeEngine = await buildFlashCards($container, {
      category,
      context: `pensum-flashcards-${category}`
    });
  }
}

async function renderCatalog($el) {
  $el.innerHTML = '<p class="empty-state">Cargando catalogo...</p>';
  const [res, missionsRes] = await Promise.all([
    LearningApiClient.getSkills(),
    MissionApiClient.list(1, 200, { status: 'active' })
  ]);
  if (!res.ok) { $el.innerHTML = `<p class="empty-state">Error: ${res.error}</p>`; return; }

  const skills = res.data?.data || [];
  let activeMissions = [];
  if (missionsRes.ok) {
    const payload = missionsRes.data?.data ?? missionsRes.data;
    const items = payload?.items || [];
    activeMissions = items.filter(m => m.status === 'active');
  }
  const listConfig = {
    id: 'nx-skill-catalog',
    columns: [
      { key: 'name', label: 'Skill', width: '30%', render: (v) => `<strong>${escHtml(v)}</strong>` },
      { key: 'category', label: 'Categoria', width: '12%', render: (v) => {
        const c = CAT_COLORS[v] || '#6e7681';
        return `<span class="task-tag" style="background:${c}22;color:${c}">${escHtml(v)}</span>`;
      }},
      { key: 'owner_type', label: 'Tipo', width: '8%', render: v => v === 'agent' ? 'agente' : 'usuario' },
      { key: 'questions_required', label: 'Preguntas', width: '10%' },
      { key: 'accuracy_required', label: 'Min %', width: '8%', render: v => `${v}%` },
      { key: 'nexos_reward', label: 'NXS', width: '8%', render: v => `<span style="color:#d29922">+${v}</span>` }
    ],
    data: skills,
    sortBy: 'category',
    sortDir: 'asc',
    emptyMessage: 'Sin skills',
    filterPlaceholder: 'Buscar skill...'
  };

  $el.innerHTML = `
    <div class="detail-section">
      <h2>Catalogo de Skills</h2>
      ${renderNxList(listConfig)}
    </div>`;

  // v2: split detail with topics + questions
  bindNxListEvents($el, 'nx-skill-catalog', listConfig, null, async (skill, detailEl) => {
    detailEl.innerHTML = '<p class="empty-state" style="font-size:0.875rem">Cargando...</p>';
    const [topicsRes, questionsRes] = await Promise.all([
      LearningApiClient.getTopics(skill.code),
      LearningApiClient.getQuestions({ category: skill.category, limit: 50, random: false })
    ]);
    const topics = topicsRes.ok ? (topicsRes.data?.data || []) : [];
    const questions = questionsRes.ok ? (questionsRes.data?.data || []) : [];
    const color = CAT_COLORS[skill.category] || '#6e7681';
    const codes = typeof skill.instruction_codes === 'string' ? JSON.parse(skill.instruction_codes) : (skill.instruction_codes || []);

    detailEl.innerHTML = `
      <h3 style="margin:0;font-size:0.95rem">${escHtml(skill.name)}</h3>
      <p style="font-size:0.875rem;color:#8b949e;margin:4px 0">${escHtml(skill.description || '')}</p>
      <div style="display:flex;gap:4px;flex-wrap:wrap;margin-bottom:8px">
        <span class="task-tag" style="background:${color}22;color:${color}">${escHtml(skill.category)}</span>
        <span class="version-badge">+${skill.nexos_reward} NXS</span>
        ${skill.owner_type === 'agent' ? '<span class="task-tag" style="background:#a371f722;color:#a371f7">agente</span>' : ''}
      </div>
      ${codes.length > 0 ? `<div style="margin-bottom:8px"><span style="font-size:0.875rem;color:#6e7681">Instrucciones:</span> ${codes.map(c => `<a href="#/instructions" class="item-used-link">${escHtml(c)}</a>`).join(' ')}</div>` : ''}
      <div style="font-size:0.875rem;font-weight:600;margin:8px 0 4px">Temas (${topics.length})</div>
      ${topics.map(t => `<div style="padding:3px 0;font-size:0.875rem;border-bottom:1px solid #21262d"><strong>${escHtml(t.name)}</strong> <span style="color:#6e7681">${t.question_count || 0} preg.</span></div>`).join('') || '<span style="font-size:0.875rem;color:#484f58">Sin temas</span>'}
      <div style="font-size:0.875rem;font-weight:600;margin:8px 0 4px">Preguntas asociadas (${questions.length})</div>
      <div style="max-height:200px;overflow-y:auto">
        ${questions.slice(0, 15).map(q => {
          const qt = q.question_text || q.questionText || '';
          return `<div style="padding:3px 0;font-size:0.875rem;border-bottom:1px solid #161b22;color:#8b949e">${escHtml(qt.substring(0, 60))}${qt.length > 60 ? '...' : ''}</div>`;
        }).join('') || '<span style="font-size:0.875rem;color:#484f58">Sin preguntas</span>'}
        ${questions.length > 15 ? `<div style="font-size:0.875rem;color:#484f58;padding-top:4px">+${questions.length - 15} mas...</div>` : ''}
      </div>
      ${linkedMissionsHtml(skill.code, activeMissions)}
    `;
  });
}

const DOMAIN_COLORS = {
  memoria: '#a371f7',
  consulta: '#58a6ff',
  rag: '#238636',
  diagnostico: '#d29922'
};

const BADGE_ICONS = {
  'Memoria Persistente': '\uD83E\uDDE0',
  'Consulta Operativa': '\u26A1',
  'Conocimiento RAG': '\uD83D\uDCDA',
  'Autodiagnostico': '\uD83D\uDD2E',
  'Gestor de Misiones': '\uD83C\uDFAF',
  'Gestor de Tareas': '\uD83D\uDCCB'
};

const DOMAIN_ICONS = {
  memoria: '\uD83E\uDDE0',
  consulta: '\u26A1',
  rag: '\uD83D\uDCDA',
  diagnostico: '\uD83D\uDD2E'
};

const VALIDATION_LABELS = {
  'agent-quality-test': 'Test Agent Quality',
  'capability-proof': 'Capacidad verificada',
  'manual': 'Validacion manual'
};

function badgeLevel(version) {
  const v = parseFloat(version || '1.0');
  return Math.floor(v);
}

async function renderAgentSkills($el) {
  $el.innerHTML = '<p class="empty-state">Cargando badges...</p>';
  const res = await AgentBadgeApiClient.list();
  if (!res.ok) { $el.innerHTML = `<p class="empty-state">Error: ${res.error || 'No se pudieron cargar los badges'}</p>`; return; }

  const badges = res.data?.data?.items || res.data?.data || [];

  // Agrupar por agente
  const byAgent = {};
  for (const b of badges) {
    const agent = b.agentCode || b.agent_code || 'unknown';
    if (!byAgent[agent]) byAgent[agent] = [];
    byAgent[agent].push(b);
  }

  const totalBadges = badges.length;
  const agents = Object.keys(byAgent);
  const domains = [...new Set(badges.map(b => b.domain))];

  // Stats
  const statsHtml = `
    <div class="banco-stats-grid" style="margin-bottom:12px">
      <div class="banco-stat-card">
        <div class="banco-stat-value">${totalBadges}</div>
        <div class="banco-stat-label">Badges totales</div>
      </div>
      <div class="banco-stat-card">
        <div class="banco-stat-value" style="color:#58a6ff">${agents.length}</div>
        <div class="banco-stat-label">Agentes con badges</div>
      </div>
      <div class="banco-stat-card">
        <div class="banco-stat-value" style="color:#d29922">${domains.length}</div>
        <div class="banco-stat-label">Dominios cubiertos</div>
      </div>
    </div>`;

  // Cards por agente
  let agentSections = '';
  for (const [agentCode, agentBadges] of Object.entries(byAgent)) {
    const badgeCards = agentBadges.map(b => {
      const color = DOMAIN_COLORS[b.domain] || '#6e7681';
      const valLabel = VALIDATION_LABELS[b.validationMethod || b.validation_method] || 'manual';
      const unlocked = b.unlockedAt || b.unlocked_at;
      const dateStr = unlocked ? formatearFecha(unlocked, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
      const caps = b.capabilityCodes || b.capability_codes || [];
      const capsCount = Array.isArray(caps) ? caps.length : 0;
      const icon = BADGE_ICONS[b.name] || DOMAIN_ICONS[b.domain] || '\uD83C\uDFC5';
      const lvl = badgeLevel(b.version);

      return `
      <div class="artifact-item" style="padding:14px;border-left:3px solid ${color}">
        <div style="display:flex;gap:14px;align-items:start">
          <div style="font-size:1.8rem;line-height:1;min-width:40px;text-align:center;filter:drop-shadow(0 0 6px ${color}66)">${icon}</div>
          <div style="flex:1">
            <div style="display:flex;justify-content:space-between;align-items:center">
              <div style="font-weight:700;font-size:0.92rem">${escHtml(b.name)}</div>
              <span style="background:${color}33;color:${color};padding:2px 8px;border-radius:10px;font-size:0.875rem;font-weight:700">Lvl ${lvl}</span>
            </div>
            <div style="font-size:0.875rem;color:#8b949e;margin-top:3px">${escHtml(b.description || '')}</div>
            <div style="margin-top:8px;display:flex;gap:6px;flex-wrap:wrap;align-items:center">
              <span class="task-tag" style="background:${color}22;color:${color};font-size:0.875rem">${escHtml(b.domain)}</span>
              <span class="task-tag" style="background:#21262d;color:#8b949e;font-size:0.875rem">${escHtml(valLabel)}</span>
              ${capsCount > 0 ? `<span style="font-size:0.875rem;color:#6e7681">${capsCount} capacidad${capsCount !== 1 ? 'es' : ''}</span>` : ''}
              ${dateStr ? `<span style="font-size:0.875rem;color:#484f58;margin-left:auto">${dateStr}</span>` : ''}
            </div>
          </div>
        </div>
      </div>`;
    }).join('');

    agentSections += `
    <div class="detail-section" style="margin-bottom:16px">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
        <span style="font-size:0.875rem;font-weight:700">${escHtml(agentCode)}</span>
        <span style="font-size:0.875rem;color:#238636">${agentBadges.length} badge${agentBadges.length !== 1 ? 's' : ''}</span>
      </div>
      <div class="artifact-list" style="gap:6px">${badgeCards}</div>
    </div>`;
  }

  $el.innerHTML = `
    <div class="detail-section">
      <h2>Badges de Agentes</h2>
      <p style="font-size:0.875rem;color:#8b949e;margin-bottom:8px">Capacidades demostradas y validadas. Cada badge representa una funcionalidad probada del agente.</p>
      ${statsHtml}
      ${agentSections || '<p class="empty-state">Sin badges registrados</p>'}
    </div>`;
}
